<template>
  <div class="face-detection-container">
    <!-- 左侧区域 -->
    <div class="left-section">
      <!-- 视频区域 -->
      <div class="video-section">
        <div class="video-wrapper">
          <video
            ref="videoRef"
            autoplay
            muted
            playsinline
            @loadedmetadata="onVideoLoaded"
            :style="{ display: cameraStatus === '运行中' ? 'block' : 'none' }"
          ></video>
          <canvas ref="canvasRef" :style="{ display: cameraStatus === '运行中' ? 'block' : 'none' }"></canvas>
          
          <!-- 摄像头未开启占位 -->
          <div v-if="cameraStatus !== '运行中'" class="video-placeholder">
            <div class="placeholder-icon">
              {{ permissionDenied ? '🚫' : cameraStatus === '启动中...' ? '⏳' : '📹' }}
            </div>
            <div class="placeholder-title">
              {{ cameraStatus === '启动中...' ? '摄像头启动中...' : 
                 permissionDenied ? '摄像头权限被拒绝' : 
                 cameraStatus === '设备未找到' ? '未检测到摄像头' :
                 cameraStatus === '设备被占用' ? '摄像头被占用' :
                 cameraStatus === '不支持' ? '浏览器不支持' :
                 '摄像头未启动' }}
            </div>
            <div class="placeholder-desc">
              {{ errorMessage || '请点击开关启动摄像头' }}
            </div>
            
            <!-- 权限被拒绝时的详细引导 -->
            <div v-if="permissionDenied" class="permission-guide">
              <p>📋 如何开启摄像头权限：</p>
              <ol>
                <li>点击浏览器地址栏左侧的 <strong>🔒 锁图标</strong> 或 <strong>ⓘ 信息图标</strong></li>
                <li>找到"摄像头"或"Camera"权限设置</li>
                <li>选择"允许"或"Allow"</li>
                <li>点击下方"重新尝试"按钮</li>
              </ol>
              <button class="retry-button" @click="retryCamera">
                🔄 重新尝试
              </button>
            </div>
          </div>
          
          <!-- 摄像头控制开关 -->
          <div class="camera-control-overlay">
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="cameraEnabled" 
                @change="handleCameraToggle" 
                aria-label="摄像头开关" 
              />
              <span class="slider"></span>
            </label>
          </div>
        </div>
      </div>

      <!-- 检测状态区域 -->
      <div class="status-section">
        <div class="status-card">
          <h3>检测状态</h3>
          <div class="status-item">
            <span class="label">摄像头状态:</span>
            <span :class="['value', cameraStatus === '运行中' ? 'success' : 'warning']">
              {{ cameraStatus }}
            </span>
          </div>
          <div class="status-item">
            <span class="label">人脸检测:</span>
            <span :class="['value', faceDetected ? 'success' : 'error']">
              {{ faceDetected ? '已检测到人脸' : '未检测到人脸' }}
            </span>
          </div>
          <div class="status-item">
            <span class="label">模型加载:</span>
            <span :class="['value', modelsLoaded ? 'success' : 'warning']">
              {{ modelsLoaded ? '已加载' : '加载中...' }}
            </span>
          </div>
          
          <!-- 等待人脸识别提示 -->
          <div v-if="!faceDetected" class="placeholder-text detection-tip">
            <p>👤 等待人脸识别中...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧：人脸信息 + 情绪分析合并区域 -->
    <div class="right-section">
      <div class="combined-card">
        <!-- 人脸信息 -->
        <div class="face-info-block">
          <h3>人脸信息</h3>
          <div v-if="faceDetected && faceInfo" class="info-grid">
            <div class="info-item">
              <span class="label">年龄:</span>
              <span class="value">约 {{ Math.round(faceInfo.age) }} 岁</span>
            </div>
            <div class="info-item">
              <span class="label">性别:</span>
              <span class="value">{{ faceInfo.gender === 'male' ? '男性' : '女性' }}</span>
            </div>
            <div class="info-item">
              <span class="label">性别置信度:</span>
              <span class="value">{{ (faceInfo.genderProbability * 100).toFixed(1) }}%</span>
            </div>
          </div>
          <div v-else class="info-grid placeholder">
            <div class="info-item">
              <span class="label">年龄:</span>
              <span class="value placeholder-value">-- 岁</span>
            </div>
            <div class="info-item">
              <span class="label">性别:</span>
              <span class="value placeholder-value">未知</span>
            </div>
            <div class="info-item">
              <span class="label">性别置信度:</span>
              <span class="value placeholder-value">--%</span>
            </div>
          </div>
        </div>

        <!-- 分割线 -->
        <div class="divider"></div>

        <!-- 情绪分析 -->
        <div class="emotion-block">
          <h3>情绪分析</h3>
          <div v-if="faceDetected && currentEmotion" class="emotion-main">
            <div class="emotion-icon">{{ getEmotionIcon(currentEmotion.emotion) }}</div>
            <div class="emotion-info">
              <div class="emotion-name">{{ getEmotionName(currentEmotion.emotion) }}</div>
              <div class="emotion-confidence">
                置信度: {{ (currentEmotion.confidence * 100).toFixed(1) }}%
              </div>
            </div>
          </div>
          <div v-else class="emotion-main placeholder">
            <div class="emotion-icon">😶</div>
            <div class="emotion-info">
              <div class="emotion-name">等待检测</div>
              <div class="emotion-confidence">请保持正对摄像头</div>
            </div>
          </div>
          
          <div class="emotion-details">
            <h4>详细情绪概率</h4>
            <div v-if="currentEmotion">
              <div 
                v-for="(value, emotion) in currentEmotion.expressions" 
                :key="emotion"
                class="emotion-bar"
              >
                <div class="emotion-label">
                  <span>{{ getEmotionName(emotion) }}</span>
                  <span>{{ (value * 100).toFixed(1) }}%</span>
                </div>
                <div class="progress-bar">
                  <div 
                    class="progress-fill" 
                    :style="{ width: `${value * 100}%` }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="placeholder-text">
              <p>📊 暂无情绪数据</p>
              <p class="hint">检测到人脸后将显示各项情绪概率</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage && cameraStatus === '运行中'" class="error-overlay">
      <div class="error-card">
        <h3>⚠️ 警告</h3>
        <p>{{ errorMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits, watch } from 'vue'
import * as faceapi from 'face-api.js'
import { message } from 'ant-design-vue'

// 接收外部属性
const props = defineProps({
  cameraEnabled: {
    type: Boolean,
    default: true
  },
  isVip: {
    type: Boolean,
    default: false
  }
})

// 定义事件
const emit = defineEmits(['update:cameraEnabled', 'onResultCall'])

// 情绪统计数据
const emotionStats = ref({
  neutral: 0,
  happy: 0,
  sad: 0,
  angry: 0,
  fearful: 0,
  disgusted: 0,
  surprised: 0
})

// 总检测次数
const totalDetections = ref(0)

// 节流控制 - 用于回调触发
let lastCallbackTime = 0
const CALLBACK_THROTTLE_MS = 5000 // 5秒触发一次回调

// 处理摄像头开关切换
const handleCameraToggle = (event) => {
  const newValue = event.target.checked
  
  // 如果要开启摄像头，检查是否是VIP
  if (newValue && !props.isVip) {
    message.warning('请开通会员后再试')
    // 阻止开关状态改变
    event.target.checked = false
    return
  }
  
  // VIP用户或关闭摄像头时允许操作
  emit('update:cameraEnabled', newValue)
}

// 响应式数据
const videoRef = ref(null)
const canvasRef = ref(null)
const cameraStatus = ref('未启动')
const faceDetected = ref(false)
const modelsLoaded = ref(false)
const currentEmotion = ref(null)
const faceInfo = ref(null)
const errorMessage = ref('')
const permissionDenied = ref(false)
const permissionStatus = ref('unknown') // 'unknown', 'granted', 'denied', 'prompt'

let stream = null
let detectionInterval = null

// 检查摄像头权限
const checkCameraPermission = async () => {
  try {
    // 检查浏览器是否支持权限API
    if (!navigator.permissions) {
      permissionStatus.value = 'unknown'
      return true // 不支持权限API，尝试直接访问
    }
    
    const result = await navigator.permissions.query({ name: 'camera' })
    permissionStatus.value = result.state
    
    // 监听权限变化
    result.onchange = () => {
      permissionStatus.value = result.state
      if (result.state === 'denied') {
        permissionDenied.value = true
        stopCamera()
        errorMessage.value = '摄像头权限已被拒绝，请在浏览器设置中允许访问摄像头'
      } else if (result.state === 'granted' && props.cameraEnabled) {
        permissionDenied.value = false
        errorMessage.value = ''
        startCamera()
      }
    }
    
    if (result.state === 'denied') {
      permissionDenied.value = true
      return false
    }
    
    return true
  } catch (error) {
    console.warn('权限检查失败:', error)
    permissionStatus.value = 'unknown'
    return true // 降级处理，尝试直接访问
  }
}

// 检测浏览器兼容性
const checkBrowserCompatibility = () => {
  // 检查是否支持 getUserMedia
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    errorMessage.value = '您的浏览器不支持摄像头功能，请使用 Chrome、Firefox、Safari 或 Edge 浏览器'
    return false
  }
  
  // 检查是否是安全环境（HTTPS、localhost 或局域网）
  const hostname = location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  const isLocalNetwork = /^192\.168\./.test(hostname) || /^10\./.test(hostname) || /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  const isSecureContext = window.isSecureContext
  
  if (!isSecureContext && !isLocalhost && !isLocalNetwork) {
    errorMessage.value = '摄像头功能需要在 HTTPS 环境下使用，当前为不安全的 HTTP 连接'
    return false
  }
  
  return true
}
const emotionNameMap = {
  neutral: '平静',
  happy: '开心',
  sad: '悲伤',
  angry: '愤怒',
  fearful: '恐惧',
  disgusted: '厌恶',
  surprised: '惊讶'
}

// 情绪图标映射
const emotionIconMap = {
  neutral: '😐',
  happy: '😊',
  sad: '😢',
  angry: '😠',
  fearful: '😨',
  disgusted: '🤢',
  surprised: '😲'
}

// 获取情绪名称
const getEmotionName = (emotion) => {
  return emotionNameMap[emotion] || emotion
}

// 获取情绪图标
const getEmotionIcon = (emotion) => {
  return emotionIconMap[emotion] || '😶'
}

// 计算情绪百分比
const calculateEmotionPercentages = () => {
  try {
    if (totalDetections.value === 0) {
      return {
        neutral: 0,
        happy: 0,
        sad: 0,
        angry: 0,
        fearful: 0,
        disgusted: 0,
        surprised: 0
      }
    }
    
    const percentages = {}
    Object.keys(emotionStats.value).forEach(emotion => {
      percentages[emotion] = ((emotionStats.value[emotion] / totalDetections.value) * 100).toFixed(2) + '%'
    })
    return percentages
  } catch (error) {
    console.error('计算情绪百分比失败:', error)
    // 返回默认值
    return {
      neutral: 0,
      happy: 0,
      sad: 0,
      angry: 0,
      fearful: 0,
      disgusted: 0,
      surprised: 0
    }
  }
}

// 加载模型
const loadModels = async () => {
  try {
    // 使用 unpkg.com 的国内镜像或备用 CDN
    // 方案1: unpkg 镜像（推荐）
    const MODEL_URL = 'https://unpkg.com/@vladmandic/face-api/model/'
    
    // 方案2: 如果 unpkg 也慢，可以使用 staticfile CDN
    // const MODEL_URL = 'https://cdn.staticfile.org/@vladmandic/face-api/1.7.12/model/'
    
    // 方案3: 使用 bootcdn（备用）
    // const MODEL_URL = 'https://cdn.bootcdn.net/ajax/libs/@vladmandic/face-api/1.7.12/model/'
    
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
      faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL)
    ])
    
    modelsLoaded.value = true
    console.log('人脸检测模型加载成功')
  } catch (error) {
    console.error('模型加载失败:', error)
    errorMessage.value = '模型加载失败，请检查网络连接或刷新页面重试'
    modelsLoaded.value = false
  }
}

// 启动摄像头
const startCamera = async () => {
  try {
    // 清空之前的错误信息
    errorMessage.value = ''
    permissionDenied.value = false
    
    // 检查浏览器兼容性
    if (!checkBrowserCompatibility()) {
      cameraStatus.value = '不支持'
      return
    }
    
    // 检查权限
    const hasPermission = await checkCameraPermission()
    if (!hasPermission) {
      cameraStatus.value = '权限被拒绝'
      errorMessage.value = '摄像头权限已被拒绝，请在浏览器设置中允许访问摄像头'
      return
    }
    
    // 如果已经有流在运行，先停止
    if (stream) {
      stopCamera()
    }
    
    cameraStatus.value = '启动中...'
    
    // 请求摄像头访问
    stream = await navigator.mediaDevices.getUserMedia({
      video: { 
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'user'
      },
      audio: false
    })
    
    if (videoRef.value) {
      videoRef.value.srcObject = stream
      cameraStatus.value = '运行中'
      permissionStatus.value = 'granted'
      errorMessage.value = ''
    }
  } catch (error) {
    console.error('摄像头启动失败:', error)
    
    // 根据错误类型提供具体的错误信息
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      cameraStatus.value = '权限被拒绝'
      permissionDenied.value = true
      permissionStatus.value = 'denied'
      errorMessage.value = '摄像头权限被拒绝，请点击地址栏的摄像头图标允许访问'
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      cameraStatus.value = '设备未找到'
      errorMessage.value = '未检测到摄像头设备，请确认摄像头已正确连接'
    } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      cameraStatus.value = '设备被占用'
      errorMessage.value = '摄像头正被其他应用占用，请关闭其他使用摄像头的程序'
    } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
      cameraStatus.value = '配置不支持'
      errorMessage.value = '摄像头不支持当前配置，尝试使用其他分辨率'
      
      // 降级尝试：使用更低的分辨率
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false
        })
        if (videoRef.value) {
          videoRef.value.srcObject = stream
          cameraStatus.value = '运行中'
          errorMessage.value = ''
        }
      } catch (fallbackError) {
        console.error('降级启动也失败:', fallbackError)
      }
    } else if (error.name === 'TypeError') {
      cameraStatus.value = '配置错误'
      errorMessage.value = '摄像头配置错误，请刷新页面重试'
    } else if (error.name === 'SecurityError') {
      cameraStatus.value = '安全限制'
      errorMessage.value = '由于安全限制无法访问摄像头，请确保在 HTTPS 环境下使用'
    } else {
      cameraStatus.value = '启动失败'
      errorMessage.value = `摄像头启动失败: ${error.message || '未知错误'}，请刷新页面重试`
    }
  }
}

// 视频加载完成
const onVideoLoaded = () => {
  try {
    if (videoRef.value && canvasRef.value) {
      const displaySize = {
        width: videoRef.value.videoWidth,
        height: videoRef.value.videoHeight
      }
      faceapi.matchDimensions(canvasRef.value, displaySize)
      startDetection()
    }
  } catch (error) {
    console.error('视频初始化失败:', error)
    errorMessage.value = '视频初始化失败，请刷新页面重试'
  }
}

// 开始人脸检测
const startDetection = () => {
  if (detectionInterval) {
    clearInterval(detectionInterval)
  }
  
  detectionInterval = setInterval(async () => {
    if (videoRef.value && canvasRef.value && modelsLoaded.value) {
      try {
        const detections = await faceapi
          .detectAllFaces(videoRef.value, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceExpressions()
          .withAgeAndGender()
        
        const displaySize = {
          width: videoRef.value.videoWidth,
          height: videoRef.value.videoHeight
        }
        
        const resizedDetections = faceapi.resizeResults(detections, displaySize)
        
        // 清空画布
        const ctx = canvasRef.value.getContext('2d')
        ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
        
        // 绘制检测结果
        faceapi.draw.drawDetections(canvasRef.value, resizedDetections)
        faceapi.draw.drawFaceLandmarks(canvasRef.value, resizedDetections)
        faceapi.draw.drawFaceExpressions(canvasRef.value, resizedDetections)
        
        // 更新检测状态
        if (detections.length > 0) {
          faceDetected.value = true
          const detection = detections[0]
          
          // 获取主要情绪
          const expressions = detection.expressions
          const maxEmotion = Object.keys(expressions).reduce((a, b) => 
            expressions[a] > expressions[b] ? a : b
          )
          
          currentEmotion.value = {
            emotion: maxEmotion,
            confidence: expressions[maxEmotion],
            expressions: expressions
          }
          
          // 更新人脸信息
          faceInfo.value = {
            age: detection.age,
            gender: detection.gender,
            genderProbability: detection.genderProbability
          }
          
          // 情绪统计：每次检测到人脸时统计主要情绪
          if (emotionStats.value[maxEmotion] !== undefined) {
            emotionStats.value[maxEmotion]++
          }
          totalDetections.value++
          
          // 节流触发回调：每1秒最多触发一次
          const now = Date.now()
          if (now - lastCallbackTime >= CALLBACK_THROTTLE_MS) {
            lastCallbackTime = now
            
            try {
              // 触发回调，传递实时采集信息
              emit('onResultCall', {
                // 当前帧信息
                current: {
                  faceInfo: faceInfo.value,
                  emotion: currentEmotion.value,
                  timestamp: now
                },
                // 情绪统计信息
                statistics: {
                  emotionStats: { ...emotionStats.value },
                  totalDetections: totalDetections.value,
                  emotionPercentages: calculateEmotionPercentages()
                }
              })
            } catch (callbackError) {
              console.error('回调函数执行失败:', callbackError)
            }
          }
        } else {
          faceDetected.value = false
          currentEmotion.value = null
          faceInfo.value = null
        }
      } catch (error) {
        console.error('人脸检测错误:', error)
        // 检测错误不影响继续运行，只记录日志
      }
    }
  }, 100) // 每100ms检测一次
}

// 停止摄像头
const stopCamera = () => {
  try {
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop()
      })
      stream = null
    }
    
    if (videoRef.value) {
      videoRef.value.srcObject = null
    }
    
    if (detectionInterval) {
      clearInterval(detectionInterval)
      detectionInterval = null
    }
    
    // 重置检测状态
    faceDetected.value = false
    currentEmotion.value = null
    faceInfo.value = null
    
    // 重置情绪统计数据
    emotionStats.value = {
      neutral: 0,
      happy: 0,
      sad: 0,
      angry: 0,
      fearful: 0,
      disgusted: 0,
      surprised: 0
    }
    totalDetections.value = 0
    
    // 重置节流计时器
    lastCallbackTime = 0
    
    if (cameraStatus.value !== '权限被拒绝' && cameraStatus.value !== '不支持') {
      cameraStatus.value = '未启动'
    }
  } catch (error) {
    console.error('停止摄像头失败:', error)
  }
}

// 重新尝试启动摄像头
const retryCamera = async () => {
  try {
    // 清除之前的错误状态
    permissionDenied.value = false
    errorMessage.value = ''
    cameraStatus.value = '启动中...'
    
    // 尝试重新启动摄像头
    await startCamera()
  } catch (error) {
    console.error('重新启动摄像头失败:', error)
    errorMessage.value = '重新启动失败，请检查权限设置'
  }
}

// 监听摄像头开关状态
watch(() => props.cameraEnabled, async (newValue) => {
  if (newValue) {
    await startCamera()
  } else {
    stopCamera()
  }
})

// 组件挂载
onMounted(async () => {
  try {
    await loadModels()
    if (props.cameraEnabled) {
      await startCamera()
    }
  } catch (error) {
    console.error('组件初始化失败:', error)
    errorMessage.value = '组件初始化失败，请刷新页面重试'
  }
})

// 组件卸载
onUnmounted(() => {
  try {
    stopCamera()
  } catch (error) {
    console.error('组件清理失败:', error)
  }
})
</script>

<style scoped lang="less">
.face-detection-container {
  width: 100%;
  height: 100%;
  display: flex;
  gap: 12px;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow: hidden;
  box-sizing: border-box;
}

/* 左侧区域 - 垂直布局 */
.left-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* 视频区域 */
.video-section {
  flex: 1;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-height: 0;
}

/* 检测状态区域 */
.status-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* 右侧：人脸信息 + 情绪分析合并区域 */
.right-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-width: 0;
  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.combined-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
  padding-bottom: 10px;
   /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.face-info-block {
  flex-shrink: 0;
}

.emotion-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.emotion-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  
  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
}

.divider {
  height: 1px;
  background: linear-gradient(to right, #f0f0f0, #e0e0e0, #f0f0f0);
  margin: 5px 0;
  flex-shrink: 0;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
}

.video-wrapper video,
.video-wrapper canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-wrapper canvas {
  pointer-events: none;
}

/* 视频占位样式 */
.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  overflow-y: auto;
}

.placeholder-icon {
  font-size: 64px;
  margin-bottom: 16px;
  margin-top: 20px;
  animation: pulse 2s ease-in-out infinite;
}

.placeholder-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.placeholder-desc {
  font-size: 14px;
  opacity: 0.9;
  text-align: center;
  padding: 0 20px;
  line-height: 1.6;
}

.permission-guide {
  margin-top: 24px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  text-align: left;
  max-width: 400px;
}

.permission-guide p {
  margin: 0 0 12px 0;
  font-weight: 600;
  font-size: 15px;
}

.permission-guide ol {
  margin: 0 0 16px 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.8;
}

.permission-guide li {
  margin-bottom: 6px;
}

.permission-guide strong {
  color: #fff;
  font-weight: 700;
}

.retry-button {
  width: 100%;
  padding: 10px 20px;
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.3);
}

.retry-button:hover {
  background: linear-gradient(135deg, #73d13d 0%, #52c41a 100%);
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.5);
  transform: translateY(-2px);
}

.retry-button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(82, 196, 26, 0.3);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

/* 视频区域内的摄像头控制开关 */
.camera-control-overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.camera-control-overlay .switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  margin: 0;
}

.camera-control-overlay .switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.camera-control-overlay .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 26px;
}

.camera-control-overlay .slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
}

.camera-control-overlay input:checked + .slider {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
}

.camera-control-overlay input:focus + .slider {
  box-shadow: 0 0 1px linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.camera-control-overlay input:checked + .slider:before {
  transform: translateX(24px);
}

/* 信息展示区 */
.status-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  padding-bottom: 30px;
   /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

h3 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

h4 {
  margin: 12px 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

/* 状态项 */
.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.status-item:last-child {
  border-bottom: none;
}

.status-item .label {
  color: #666;
  font-size: 13px;
}

.status-item .value {
  font-weight: 600;
  font-size: 13px;
}

.value.success {
  color: #52c41a;
}

.value.warning {
  color: #faad14;
}

.value.error {
  color: #ff4d4f;
}

/* 情绪展示 */
.emotion-main {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px;
  margin-bottom: 5px;
}

.emotion-main.placeholder {
  background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
}

.emotion-icon {
  font-size: 40px;
  line-height: 1;
}

.emotion-info {
  flex: 1;
  color: #fff;
}

.emotion-name {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.emotion-confidence {
  font-size: 13px;
  opacity: 0.9;
}

/* 情绪详细信息 */
.emotion-details {
  margin-top: 2px;
  h4 {
    padding: 4px;
    margin: 0;
    font-weight: 600;
  }
}

.emotion-bar {
  margin-bottom: 8px;
}

.emotion-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: #666;
}

.progress-bar {
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* 人脸信息网格 */
.info-grid {
  display: grid;
  gap: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.info-item .label {
  color: #666;
  font-size: 13px;
}

.info-item .value {
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.info-item .value.placeholder-value {
  font-weight: 600;
  color: #999;
  font-size: 13px;
}

/* 占位文本样式 */
.placeholder-text {
  text-align: center;
  padding: 16px 12px;
  color: #999;
  background: #f8f9fa;
  border-radius: 6px;
  margin-top: 8px;
}

.placeholder-text.detection-tip {
  margin-top: 12px;
}

.placeholder-text p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.placeholder-text p:first-child {
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
}

.placeholder-text .hint {
  font-size: 12px;
  color: #aaa;
}

/* 错误提示覆盖层 */
.error-overlay {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  max-width: 400px;
}

.error-card {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.error-card h3 {
  color: #ff4d4f;
}

.error-card p {
  margin: 0;
  color: #666;
  line-height: 1.6;
}
</style>
